import { computed, ref, watch } from 'vue'
import type { ComputedRef, Ref } from 'vue'
import { CallType } from '@/stores/call'
import type { ConferenceEntry } from '@/stores/call'
import type { Contact, CallContact } from '@/services/types/Contact'

interface CallDisplayInput {
  activeCall: ComputedRef<ConferenceEntry | null>
  heldCall: ComputedRef<ConferenceEntry | null>
  ringingCall: ComputedRef<ConferenceEntry | null>
  incomingRingingCallId: Ref<string | null>
}

export function useCallDisplay ({ activeCall, heldCall, ringingCall, incomingRingingCallId }: CallDisplayInput) {
  const isConference = computed(() => (activeCall.value?.conferenceParticipants.length ?? 0) > 1)
  const isIncomingRinging = computed(() => !!ringingCall.value && !activeCall.value && !heldCall.value)
  const isNewIncomingCall = computed(() => !!ringingCall.value && (!!activeCall.value || !!heldCall.value))
  const isOnHold = computed(() => !!heldCall.value)

  const ringingParticipants = computed(() => ringingCall.value?.conferenceParticipants ?? [])
  const activeParticipants = computed(() => activeCall.value?.conferenceParticipants ?? ringingCall.value?.conferenceParticipants ?? [])
  const heldParticipants = computed(() => heldCall.value?.conferenceParticipants ?? [])

  const extraActiveParticipantsText = computed(() => {
    const count = activeParticipants.value.length - 1
    if (count <= 0) { return '' }
    return `and ${count} other${count === 1 ? '' : 's'}`
  })
  const extraHeldParticipantsText = computed(() => {
    const count = heldParticipants.value.length - 1
    if (count <= 0) { return '' }
    return `and ${count} other${count === 1 ? '' : 's'}`
  })

  const callTitle = computed(() => {
    if (isIncomingRinging.value) { return 'Someone is Calling You' }
    if (isConference.value) { return 'Conference Call' }
    return 'Call in Progress'
  })

  const callInProgress = computed(
    () => !!activeCall.value || !!incomingRingingCallId.value?.startsWith('CF')
  )

  const dialerOpen = ref(false)
  const callSidebarOpen = ref(false)
  watch([callInProgress, dialerOpen], ([inProgress, dialer]) => {
    callSidebarOpen.value = inProgress || dialer
  })

  const outgoingCall = computed(() =>
    activeCall.value?.type === CallType.OUTGOING ? activeCall.value.call : null
  )
  const currentOutgoingContact = ref<Contact | CallContact | null>(null)
  const lastCompletedOutgoingContact = ref<Contact | CallContact | null>(null)
  watch(activeCall, (call) => {
    if (call?.type === CallType.OUTGOING) {
      currentOutgoingContact.value = call.conferenceParticipants[0]?.contact ?? null
    }
  })
  watch(outgoingCall, (call, previousCall) => {
    if (!call && previousCall && currentOutgoingContact.value) {
      lastCompletedOutgoingContact.value = currentOutgoingContact.value
      currentOutgoingContact.value = null
    }
  })

  return {
    isIncomingRinging,
    isNewIncomingCall,
    isOnHold,
    ringingParticipants,
    activeParticipants,
    heldParticipants,
    extraActiveParticipantsText,
    extraHeldParticipantsText,
    callTitle,
    callInProgress,
    dialerOpen,
    callSidebarOpen,
    lastCompletedOutgoingContact
  }
}
